import type { FaresPageFlight } from '../fares/fares_page_shared.js';
import type { FlightAirportSwitchFn } from '../fares/fares_request_page.js';
import type { FlightSegmentSource } from '../results/flight_offer_source.js';
import { liveOfferForPick } from '../results/flight_identity.js';
import type { LegStep } from '../results/leg_stepper.js';
import type { FlightAirportSwitchView } from '../results/offer/offer_copy.js';
import type { ExplorerState } from './explorer_state.js';
import type { FlightExplorerOffer } from './search_source.js';

/**
 * Seletores do STEPPER e da tela de tarifas sobre o estado do explorer — leitura
 * pura. Fora de `explorer.tsx` porque ele está no teto de linhas.
 */

/** Ofertas de UMA perna específica (a ida segue legível com a volta ativa). A
 *  semente conta: o chip do passo tem de saber desenhar um pick feito sobre ela. */
export function activeOffersOfLeg(state: ExplorerState, index: number): FlightExplorerOffer[] {
  const leg = state.legs[index];
  return leg?.phase === 'ready' || leg?.phase === 'seeded' ? leg.offers : [];
}

/** A oferta do pick da perna: na lista viva, senão na SEMENTE. As refs são por
 *  sessão do modal-session, então o pick do host (sessão do agente) nunca está na
 *  lista viva da bancada — sem a semente o chip do passo ficaria órfão logo após
 *  o pouso da busca de mount (spec bancada-host-pick). */
function pickedOfferOf(state: ExplorerState, leg: number): FlightExplorerOffer | undefined {
  const picked = state.chosen[leg]?.offerId;
  if (picked === undefined) return undefined;
  const byId = (o: FlightExplorerOffer) => o.offerId === picked;
  return activeOffersOfLeg(state, leg).find(byId) ?? (state.seeds[leg] ?? []).find(byId);
}

/**
 * O VOO que fechou uma perna — o insumo do chip de detalhe no passo do stepper.
 *
 * Na fase 2 do round-trip o ref escolhido é o PAR, e o voo daquele passo é a VOLTA
 * dele: ler `segments[0]` ali mostraria a ida de novo, com o rótulo da volta. O
 * `??` não é zelo — oferta de fase 2 SEM `returnLeg` não é par nenhum, e aí o
 * trecho dela é o próprio `segments[0]` (é o que o provider devolve quando
 * precifica a volta como uma perna solta).
 */
export function chosenFlightOf(state: ExplorerState, leg: number): FlightSegmentSource | undefined {
  const offer = pickedOfferOf(state, leg);
  if (!offer) return undefined;
  if (state.request.mode === 'round_trip' && leg === 1) {
    return offer.returnLeg?.segments[0] ?? offer.segments[0];
  }
  return offer.segments[0];
}

/**
 * O voo escolhido de UMA perna no shape que a tela de tarifas consome — o ref
 * opaco (pros drawers de detalhe e assentos), o trecho e as paradas.
 *
 * Na fase 2 do round trip o ref é o PAR, e o trecho DELE é a volta: ler
 * `segments[0]` ali mostraria a ida de novo, com o rótulo da volta (mesma
 * armadilha do `chosenFlightOf`). `null` quando o pick não resolve contra a
 * lista viva nem contra a SEMENTE — a tela não desenha um voo que a busca não
 * mostra mais. A semente conta pelo mesmo motivo do `chosenFlightOf`: o pick do
 * host (o card do roteiro) vem de outra sessão de busca.
 */
export function faresFlightOf(state: ExplorerState, leg: number): FaresPageFlight | null {
  const offer = pickedOfferOf(state, leg);
  if (!offer) return null;
  const back = state.request.mode === 'round_trip' && leg === 1 ? offer.returnLeg : undefined;
  const seg = back?.segments[0] ?? offer.segments[0];
  if (!seg) return null;
  const stops = back
    ? (back.stops ?? back.segments.length - 1)
    : (offer.stops ?? offer.segments.length - 1);
  return { offerId: offer.offerId, seg, stops };
}

/**
 * Passos do stepper: RT = ida+volta derivadas do pedido; multi = um por perna.
 * one_way não tem stepper (um passo não é uma fila).
 *
 * O que cada passo leva quando está escolhido: o VOO (sempre) e o PREÇO (quando
 * existe). A ida do RT tem voo e não tem preço — o par só precifica na fase 2 —, e
 * é justo por isso que o `LegStep` conta o resolvido pelos dois campos: amarrar ao
 * preço deixava a ida escolhida contando como pendente.
 */
export function stepsFor(state: ExplorerState): LegStep[] | null {
  const chosenOf = (i: number): Pick<LegStep, 'chosenPrice' | 'chosenFlight'> => {
    const summary = state.chosen[i]?.summary;
    const flight = chosenFlightOf(state, i);
    return {
      ...(summary ? { chosenPrice: { amount: summary.total, currency: summary.currency } } : {}),
      ...(flight ? { chosenFlight: flight } : {}),
    };
  };
  const first = state.request.legs[0];
  if (state.request.mode === 'round_trip' && first) {
    return [
      {
        origin: first.origin,
        destination: first.destination,
        departDate: first.departDate,
        ...chosenOf(0),
      },
      {
        origin: first.destination,
        destination: first.origin,
        departDate: state.request.returnDate ?? '',
        ...chosenOf(1),
      },
    ];
  }
  if (state.request.mode === 'multi_leg') {
    return state.request.legs.map((l, i) => ({
      origin: l.origin,
      destination: l.destination,
      departDate: l.departDate,
      ...chosenOf(i),
    }));
  }
  return null;
}

/**
 * A troca de aeroporto (#4203) de cada linha da FASE 2 do ida e volta: a volta
 * parte de um aeroporto diferente daquele em que a ida escolhida chega. A linha
 * da fase 2 é a volta projetada como perna (sem `returnLeg`) ou o combo — por
 * isso o `??`, a mesma leitura do `chosenFlightOf`. `undefined` fora da fase 2
 * (nada a comparar).
 */
export function returnSwitchesOf(
  state: ExplorerState,
  airportSwitchOf: FlightAirportSwitchFn,
): ((offer: FlightExplorerOffer) => FlightAirportSwitchView | null) | undefined {
  if (state.request.mode !== 'round_trip' || state.activeLeg !== 1) return undefined;
  const outboundArrival = chosenFlightOf(state, 0)?.to;
  if (!outboundArrival) return undefined;
  const requested = state.request.legs[0]?.destination;
  return (offer) =>
    airportSwitchOf({
      outboundArrival,
      returnDeparture: (offer.returnLeg?.segments[0] ?? offer.segments[0])?.from,
      requested,
    });
}

/**
 * O spread por linha para o `OfferListBody` do explorer — fora de
 * `ExplorerBody` (teto de complexidade). A linha do voo escolhido (spec
 * bancada-host-pick): por ref exato, senão pelo MESMO voo em outra sessão — o
 * pick do host vem com ref da sessão do agente e a lista viva é da bancada. RT
 * fica de fora: a fase 2 é lista de PARES, e lá a linha ganha a troca de
 * aeroporto (`returnSwitchesOf`).
 */
export function rowPropsOf(
  state: ExplorerState,
  rows: FlightExplorerOffer[],
  switchOf: FlightAirportSwitchFn,
): {
  chosenOfferId?: string;
  airportSwitchOf?: (offer: FlightExplorerOffer) => FlightAirportSwitchView | null;
} {
  if (state.request.mode === 'round_trip') {
    const airportSwitchOf = returnSwitchesOf(state, switchOf);
    return airportSwitchOf ? { airportSwitchOf } : {};
  }
  const live = liveOfferForPick(
    state.chosen[state.activeLeg],
    rows,
    state.seeds[state.activeLeg] ?? [],
  );
  return live ? { chosenOfferId: live.offerId } : {};
}
